/**
 * Dispose GPU resources under an Object3D (and, for Scenes, background/environment).
 * Resources flagged as shared (userData.evoShared === true, set by Assets for cached GLBs)
 * are skipped. Covers textures on material properties AND in ShaderMaterial uniforms
 * (including arrays and render targets), plus Mesh/Points/Line/Sprite geometry, SkinnedMesh
 * skeleton bone textures and light shadow maps.
 */
import { BufferGeometry, Light, Material, Scene, SkinnedMesh, Sprite, Texture, WebGLRenderTarget, type Object3D } from 'three';

const shared = (o: { userData?: Record<string, unknown> } | undefined | null) => !!o?.userData?.evoShared;

function disposeValue(v: unknown, seen: Set<unknown>): void {
  if (!v || typeof v !== 'object' || seen.has(v)) return;
  seen.add(v);
  if (v instanceof Texture) {
    if (!shared(v)) v.dispose();
  } else if (v instanceof WebGLRenderTarget) {
    v.dispose();
  } else if (Array.isArray(v)) {
    for (const x of v) disposeValue(x, seen);
  }
}

function disposeMaterial(m: Material, seen: Set<unknown>): void {
  if (seen.has(m) || shared(m)) return;
  seen.add(m);
  for (const v of Object.values(m as unknown as Record<string, unknown>)) disposeValue(v, seen);
  const uniforms = (m as unknown as { uniforms?: Record<string, { value: unknown }> }).uniforms;
  if (uniforms) for (const u of Object.values(uniforms)) disposeValue(u?.value, seen);
  m.dispose();
}

export function disposeObject(root: Object3D): void {
  const seen = new Set<unknown>();
  root.traverse((o) => {
    const any = o as unknown as { geometry?: BufferGeometry; material?: Material | Material[] };
    // Never dispose a Sprite's geometry: three.js shares ONE module-level geometry across every
    // Sprite, so freeing it breaks all sprites in the next stage (WebGPU: "Buffer used in submit
    // while destroyed", #143).
    if (!(o instanceof Sprite) && any.geometry instanceof BufferGeometry && !shared(any.geometry) && !seen.has(any.geometry)) {
      seen.add(any.geometry);
      any.geometry.dispose();
    }
    // Shadow-caster overrides (e.g. @fx wind sway) aren't reachable via .material.
    const custom = o as unknown as { customDepthMaterial?: Material; customDistanceMaterial?: Material };
    if (custom.customDepthMaterial) disposeMaterial(custom.customDepthMaterial, seen);
    if (custom.customDistanceMaterial) disposeMaterial(custom.customDistanceMaterial, seen);
    // Skinned GLBs: each Skeleton owns a bone DataTexture that geometry/material disposal misses.
    if (o instanceof SkinnedMesh && o.skeleton && !seen.has(o.skeleton)) {
      seen.add(o.skeleton);
      o.skeleton.dispose();
    }
    // Shadow-casting lights own a shadow map render target.
    if (o instanceof Light) {
      (o as Light & { shadow?: { dispose?: () => void } }).shadow?.dispose?.();
    }
    const mat = any.material;
    if (Array.isArray(mat)) mat.forEach((m) => m instanceof Material && disposeMaterial(m, seen));
    else if (mat instanceof Material) disposeMaterial(mat, seen);
  });
  if (root instanceof Scene) {
    disposeValue(root.background, seen);
    disposeValue(root.environment, seen);
    root.background = null;
    root.environment = null;
  }
}
